/**
 * The asset library: fonts (with glyph coverage for the configured languages), images and video; upload by button
 * or drop. Images and video are added to the canvas; a font is applied to the selected text.
 */
import { useEffect, useState, useSyncExternalStore, type DragEvent } from 'react';
import { Check, Film, Plus, TriangleAlert, Upload } from 'lucide-react';
import type { LibraryAsset } from '../api.ts';
import { api, ApiError } from '../api.ts';
import { formatBytes, t } from '../i18n.ts';
import { onTablistKeyDown, tabProps } from '../ui/controls.tsx';
import { ensureAsset, patchElement } from '../model/commands.ts';
import { findElement } from '../model/tree.ts';
import { addElement } from '../state/actions.ts';
import { assetCache } from '../state/assets.ts';
import { sceneView, useEditor, type EditorStore } from '../state/store.ts';

const ACCEPT = '.woff2,.ttf,.otf,.png,.jpg,.jpeg,.webp,.svg,.webm,.mp4';

function Coverage({ asset, languages }: { asset: LibraryAsset; languages: string[] }) {
  const cov = asset.meta.coverage;
  if (!cov) return null;
  return (
    <span className="coverage" data-testid="font-coverage">
      {languages.map((lang) => {
        const c = cov[lang];
        const ok = c?.complete ?? false;
        return (
          <span key={lang} className={`lang ${ok ? 'ok' : 'missing'}`} title={ok ? t('assets.coverageOk', { lang }) : t('assets.coverageMissing', { lang, chars: (c?.missing ?? []).join(' ') })}>
            {ok ? <Check size={10} aria-hidden /> : <TriangleAlert size={10} aria-hidden />} {lang}
          </span>
        );
      })}
    </span>
  );
}

export function AssetsPanel({ store, languages }: { store: EditorStore; languages: string[] }) {
  useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  const selection = useEditor(store, (s) => s.selection);
  const scene = useEditor(store, sceneView);
  const [kind, setKind] = useState<'font' | 'image' | 'video'>('font');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string }>();
  const [over, setOver] = useState(false);
  const items = assetCache.library.filter((a) => a.kind === kind);
  const selectedText = selection.map((id) => findElement(scene, id)).filter((el) => el?.type === 'text');

  const upload = async (files: FileList | File[]) => {
    setBusy(true);
    setMessage(undefined);
    const done: string[] = [];
    try {
      for (const f of Array.from(files)) {
        try {
          const a = await api.uploadAsset(f);
          done.push(a.originalName);
          setKind(a.kind);
        } catch (e) {
          const msg = e instanceof ApiError ? e.message : String(e);
          setMessage({ ok: false, text: t('assets.uploadFailed', { name: f.name, message: msg }) });
        }
      }
      await assetCache.refreshLibrary();
      if (done.length) setMessage((m) => m ?? { ok: true, text: t('assets.uploaded', { names: done.join(', ') }) });
    } finally {
      setBusy(false);
    }
  };

  const applyFont = (a: LibraryAsset) => {
    const ids = selectedText.map((el) => el!.id);
    store.apply((s) => {
      let next = ensureAsset(s, a.sceneAsset);
      for (const id of ids) next = patchElement(next, id, (el) => (el.type === 'text' ? { ...el, style: { ...el.style, font: a.hash } } : el));
      return next;
    });
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (e.dataTransfer.files.length) void upload(e.dataTransfer.files);
  };

  return (
    <div
      className={`assets${over ? ' drop-over' : ''}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      data-testid="assets-panel"
    >
      <div className="assets-head">
        <div className="segmented" role="tablist" aria-label={t('assets.kinds')} onKeyDown={onTablistKeyDown}>
          {(['font', 'image', 'video'] as const).map((k) => (
            <button key={k} type="button" {...tabProps(kind === k, 'assets-list')} id={`assets-tab-${k}`} className={kind === k ? 'active' : ''} onClick={() => setKind(k)} data-testid={`assets-tab-${k}`}>
              {t(`assets.kind.${k}`)}
            </button>
          ))}
        </div>
        <label className="btn small upload" title={t('assets.uploadHint')}>
          <Upload size={13} aria-hidden /> {busy ? t('assets.uploading') : t('assets.upload')}
          <input
            type="file"
            accept={ACCEPT}
            multiple
            className="sr-only"
            data-testid="asset-upload"
            onChange={(e) => {
              if (e.target.files?.length) void upload(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {message && (
        <p className={`assets-msg ${message.ok ? 'ok' : 'err'}`} role={message.ok ? 'status' : 'alert'}>
          {message.text}
        </p>
      )}
      <div id="assets-list" role="tabpanel" aria-labelledby={`assets-tab-${kind}`} className="asset-list-panel">
      <ul className="asset-list" aria-label={t(`assets.kind.${kind}`)}>
        {!assetCache.libraryLoaded && <li className="empty">{t('assets.loading')}</li>}
        {assetCache.libraryLoaded && items.length === 0 && <li className="empty">{t('assets.empty')}</li>}
        {items.map((a) => {
          const inScene = scene.assets.some((s) => s.hash === a.hash);
          return (
            <li key={a.hash} className="asset" data-testid="asset-item" data-hash={a.hash}>
              {a.kind === 'image' && <AssetThumb asset={a} />}
              {a.kind === 'video' && <Film size={28} aria-hidden className="thumb-icon" />}
              <div className="asset-info">
                <span className="asset-name" title={a.originalName}>
                  {a.kind === 'font' ? `${a.meta.family ?? a.originalName}${a.meta.subfamily && a.meta.subfamily !== 'Regular' ? ` ${a.meta.subfamily}` : ''}` : a.originalName}
                </span>
                <span className="asset-meta">
                  {a.kind === 'font' ? `${a.meta.fontWeight ?? ''}${a.meta.variable ? ` · ${t('assets.variable')}` : ''}` : a.meta.width ? `${a.meta.width} × ${a.meta.height}` : a.mime}
                  {' · '}
                  {formatBytes(a.size)}
                  {a.builtin ? ` · ${t('assets.builtin')}` : ''}
                  {inScene ? ` · ${t('assets.inUse')}` : ''}
                </span>
                {a.kind === 'font' && <Coverage asset={a} languages={languages} />}
              </div>
              {a.kind === 'font' ? (
                <button type="button" className="btn small" disabled={selectedText.length === 0} onClick={() => applyFont(a)} title={t('assets.applyFontHint')}>
                  {t('assets.applyFont')}
                </button>
              ) : (
                <button type="button" className="btn small" onClick={() => addElement(store, a.kind === 'image' ? 'image' : 'video', { asset: a })} data-testid="asset-add" aria-label={t('assets.addNamed', { name: a.originalName })}>
                  <Plus size={13} aria-hidden /> {t('assets.add')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      </div>
      <p className="hint">{t('assets.dropHint')}</p>
    </div>
  );
}

function AssetThumb({ asset }: { asset: LibraryAsset }) {
  useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  useEffect(() => assetCache.ensure([asset.sceneAsset]), [asset]);
  return <img className="thumb" src={assetCache.imageUrl(asset.hash)} alt="" />;
}
